import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AuthenticationService, MemberPacService, TenantService } from '@core/service';
import { MemberSearch, PrimaryCarePhysician, MemberBenefit } from '@core/model';
import { AddressType } from '@core/enumeration/address-type';
import { EmployerIdentifier, FundingTypes } from '@shared/enumeration';
import { AuthorizationData } from '@shared/model';
import { calculateAge } from '@shared/utility';
import { StateShortName, Tenant, TenantNames, UtcToLocalPipe } from '@mynexus/mynexus-ui-lib';
import { LineOfBusiness} from '@core/enumeration';

import { getDifferenceInDays } from '@shared/utility/date-difference';
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-member-detail-header',
    templateUrl: './member-detail-header.component.html',
    styleUrls: ['member-detail-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MemberDetailHeaderComponent implements OnChanges{
  @Input() memberSearch: MemberSearch | AuthorizationData;
  @Input() tenantId: string;
  @Input() primaryCarePhysician: PrimaryCarePhysician;
  @Input() utilizationReviewNumber: string;
  groupId : string;
  employerIdentifier: string;
  isExternal: boolean;
  showOHIOIdentifier = false;
  isOHIO = false;
  specialtyProgram!: string;
  isErisaIndicator= false;
  employeWaGroup = ['W12667', 'W12652', 'W12653', 'W24174']
  showWaGroupIdentifier = false;
  readonly groupIdApplicableTenants : TenantNames[] = [TenantNames.Anthem, TenantNames.Aetna, TenantNames.Regence];
  pTooltipMessage = "Download Explanation Of Coverage";
  private maStateMandateBenefitPlans: string[] = [];

  get isEscalated(): boolean {
    return this.memberSearch.memberDTO.isEscalatedCase;
  }

  get memberBenefit (): MemberBenefit {
    return (this.memberSearch as MemberSearch)?.memberDetailsDTO?.memberBenefit
      || (this.memberSearch as AuthorizationData)?.memberBenefitDTO ;
  }

  // As we have double interface 1st case is for MemberSearch, 2nd is for AuthorizationData
  get isCommercialLOB(): boolean {
    return this.memberBenefit?.lineOfBusiness === LineOfBusiness.Commercial || this.memberSearch.memberDTO?.lineOfBusiness === LineOfBusiness.Commercial;
  }

  get lineOfBusiness(): { text: string; class: string; } | null {
    // 3rd part need for Home Health authorization
    const lineOfBusinessApplicationKey = this.memberBenefit?.lineOfBusinessApplicationKey || this.memberSearch.memberDTO?.['lineOfBusinessApplicationKey'];
    const lineOfBusiness = this.memberBenefit?.lineOfBusiness || this.memberSearch.memberDTO.lineOfBusiness
    const mcoState = this.memberBenefit?.mcoState || this.memberSearch.memberDTO?.['mcoState'];
    const mcoName = this.memberBenefit?.mcoName || this.memberSearch.memberDTO?.['mcoName'];

    if (!lineOfBusiness) {
      return null;
    }

    switch (lineOfBusinessApplicationKey || lineOfBusiness) {
      case LineOfBusiness.MedicareAdvantage:
        return { text: 'MA', class: 'ma' };

      case LineOfBusiness.Commercial:
        return { text: lineOfBusiness, class: 'commercial' };

      default:
        return {
          text: mcoState && mcoName ? `Medicaid ${mcoState} / ${mcoName}` : lineOfBusiness,
          class: 'active'
        }
    }
  }

  get showCovaIdentifier():boolean {
    return this.isCommercialLOB && this.employerIdentifier === EmployerIdentifier.Cova;
  }

  get healthPlanName(): string {
    return this.memberBenefit?.healthPlan || this.memberSearch.memberDTO?.healthPlanName;
  }

  get transformBenefitPlanEffectiveDate(): string {
    const startDate = this.memberBenefit?.benefitPlanEffectiveStartDate;
    const endDate = this.memberBenefit?.transitionPeriod;
    return `${this.utcToLocal.transform(startDate, 'MM/dd/yy')}-${this.utcToLocal.transform(endDate, 'MM/dd/yy')}`
  }

  get residentialAddress() {
    return (
      (this.memberSearch as AuthorizationData).memberAddresses ||
      (this.memberSearch as MemberSearch).memberAddressDTO
    )?.find(a => a.addressType === AddressType.Residential);
  }
  eocDeeplink = '';
  constructor(
    authentication: AuthenticationService,
    private readonly memberPacService:MemberPacService,
    private readonly ref: ChangeDetectorRef,
    private readonly utcToLocal: UtcToLocalPipe,
    private tenantService: TenantService
  ) {
    this.isExternal = authentication.isExternalUser;
  }

  ngOnInit(): void {
    this.loadMaStateMandateConfiguration();
  }

  private loadMaStateMandateConfiguration(): void {
    this.memberPacService.getMaStateMandateBenefitPlans(TenantNames.Anthem)
      .subscribe({
        next: (plans) => {
          this.maStateMandateBenefitPlans = plans || [];
          this.ref.detectChanges();
        },
        error: () => {
          this.maStateMandateBenefitPlans = [];
          this.ref.detectChanges();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { memberSearch } = changes;
    const benefitPlanName = memberSearch?.currentValue?.memberDTO?.benefitPlanName;
    const lineOfBusinessName = this.memberBenefit?.lineOfBusiness || this.memberSearch.memberDTO.lineOfBusiness;

    const processedPrograms = this.manageSdohSpecialtyProgram(
      this.memberSearch.memberDTO?.specialtyProgram,
      this.healthPlanName,
      this.memberSearch.memberDTO?.isEligible,
      lineOfBusinessName
    );
    this.specialtyProgram = processedPrograms?.join(', ');

    if (memberSearch && memberSearch.currentValue && this.isCommercialLOB) {
      this.getERISAReferenceDetails(memberSearch.currentValue.memberDTO.memberId);
    }

    this.isOHIO = benefitPlanName === 'QB0BW59989M001';
  }

  getAge(birthDate: string): string {
    return `${calculateAge(birthDate)} year(s) old`;
  }

  get getTooltipMessage() : string {
    const memberDetail : MemberSearch = this.memberSearch ;
    if(memberDetail != null && this.eocDeeplink === '') {
     this.memberPacService.getEOCExplanation(this.tenantId , memberDetail?.memberDetailsDTO?.memberBenefit?.benefitPlan)
    .subscribe((res) => {
       if(res === null) {
         this.eocDeeplink = res;
         this.pTooltipMessage = "Document unavailable, please refer to the EOC process/job aid" ;
         this.ref.detectChanges();
       }else {
         this.eocDeeplink = res
       }
     })
    }
    return  this.pTooltipMessage
  }

  downloadDocument() {
    if(this.eocDeeplink !== null) {
      const link = document.createElement('a');
      link.setAttribute('target', '_blank');
      link.setAttribute('href', this.eocDeeplink);
      link.setAttribute('download',"explanationOfCoverageDocument");
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }

  private getERISAReferenceDetails(memberId: string) {
    this.memberPacService.getERISAReferenceDetails(this.tenantId, memberId).subscribe(res => {
      if(res) {
        this.employerIdentifier = res.employerIdentifier?.trim();
        this.showOHIOIdentifier = res.employerIdentifier?.trim() === EmployerIdentifier.OHIO;
        this.showWaGroupIdentifier = this.employeWaGroup.includes(res.employerIdentifier?.trim());
        this.isErisaIndicator = res.isERISA;
        this.ref.detectChanges();
      }
    });
  }
  shouldShowGroupId(): boolean {
    if (this.memberSearch['memberDetailsDTO'] && Array.isArray(this.memberSearch['memberDetailsDTO'].memberEligibility) && this.memberSearch['memberDetailsDTO'].memberEligibility.length > 0) {
      this.groupId = this.memberSearch['memberDetailsDTO'].memberEligibility[0].groupId;
    } else if (Array.isArray(this.memberSearch['eligibilityList']) && this.memberSearch['eligibilityList'].length > 0) {
      this.groupId = this.memberSearch['eligibilityList'][0].groupId;
    } else {
      // Handle the case where neither condition is met
      this.groupId = null;
    }
    return this.groupIdApplicableTenants.includes(this.healthPlanName as TenantNames) && this.lineOfBusiness?.text === "MA";
  }

  showFundingIndicator(): boolean {
    const eligibleStates = [
      StateShortName.Maryland,
      StateShortName.Colorado,
      StateShortName.NewHampshire,
      StateShortName.Georgia,
      StateShortName.Kentucky,
      StateShortName.Maine,
      StateShortName.Indiana,
      StateShortName.Nevada
    ];

    const fundingType = this.memberBenefit?.fundingArrangement;
    const isEligibleFunding = fundingType === FundingTypes.FI || fundingType === FundingTypes.ASO;

    return eligibleStates.includes(this.memberBenefit?.programState as StateShortName) && isEligibleFunding;
  }

  getFundingTypeTooltipText(): string {
    const fundingType = this.memberBenefit?.fundingArrangement;

    switch (fundingType) {
      case FundingTypes.FI:
        return 'Fully Insured';
      case FundingTypes.ASO:
        return this.isErisaIndicator ? 'Erisa ASO' : 'Non-Erisa ASO';
      default:
        return '';
    }
  }


  checkStatusCancelled(): boolean {
    return this.tenantService.getTenantName(this.tenantId) == TenantNames.Aetna &&
      getDifferenceInDays(this.memberSearch.memberDTO.effectiveBeginDate, this.memberSearch.memberDTO.effectiveEndDate) === 0;
  }

  private manageSdohSpecialtyProgram(
    specialtyPrograms: string[] | null | undefined,
    healthPlanName: string | null | undefined,
    isEligible: boolean | null | undefined,
    lob: string | null | undefined
  ): string[] {
    let programs = specialtyPrograms ? [...specialtyPrograms] : [];

    const isAnthemTenant = healthPlanName?.toLowerCase() === TenantNames.Anthem.toLowerCase();
    const isActiveMember = isEligible === true;
    const isMedicareAdvantageLOB = lob?.toLowerCase() === LineOfBusiness.MedicareAdvantage.toLowerCase();
    const qualifiesForSdoh = isAnthemTenant && isActiveMember && isMedicareAdvantageLOB;

    if (qualifiesForSdoh) {
      if (!programs.some(p => p?.toLowerCase() === 'sdoh')) {
        programs.push('SDoH');
      }
    } else {
      programs = programs.filter(p => p?.toLowerCase() !== 'sdoh');
    }
    
    return programs;
  }

  showInMandateApplies(): boolean {
    return (
      this.memberBenefit?.programState === StateShortName.Indiana &&
      this.isCommercialLOB
    );
  }

  showMaStateMandateMessage(): boolean {
    const benefitPlan = this.memberBenefit?.benefitPlan || this.memberSearch.memberDTO?.benefitPlanName;
    return (
      Array.isArray(this.maStateMandateBenefitPlans) &&
      this.maStateMandateBenefitPlans.includes(benefitPlan) &&
      this.lineOfBusiness?.text === 'MA' &&
      this.healthPlanName === TenantNames.Anthem
    );
  }
}
